/**
 * Boot polyfills for old iOS WebViews.
 *
 * Must be imported FIRST in main.tsx — ES module imports execute in
 * source order, so loading this before any other import guarantees the
 * shims are installed before a dependency's module-level code can call
 * the missing method.
 *
 * Why this exists: Sentry issue TRYBEUP-CAPACITOR-7 —
 * `TypeError: this.o.at is not a function`. We don't call `.at()`
 * anywhere in our own source (grep is clean), so it comes from a
 * bundled dependency calling `Array.prototype.at` / `String.prototype.at`.
 * Both methods landed in Safari 15.4; a visitor on iOS 15.0–15.3 hits a
 * hard crash. `.at()` shipped alongside a small cluster of other ES2022
 * methods, so we shim the ones most likely to be reached by modern
 * libraries on the same old-WebView cohort.
 *
 * Each shim is guarded — if the engine already has the method (every
 * iOS 15.4+ / modern browser), we touch nothing. Zero cost on current
 * devices; the whole module is a few hundred bytes.
 *
 * The `any` casts below are deliberate: this project's tsconfig `lib`
 * target predates ES2022, so `.at` / `Object.hasOwn` aren't in the type
 * definitions — which is exactly why the runtime is missing them on old
 * engines. We reach past the types to install the runtime shim.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Array.prototype.at / String.prototype.at (Safari 15.4) ───────────────
// Negative index counts from the end; out-of-range returns undefined —
// matching the spec so dependency code behaves identically to a native
// engine.
function atShim(this: { length: number; [i: number]: unknown }, index: number) {
  const len = this.length;
  // ToInteger: NaN / undefined → 0, truncate toward zero.
  let i = Math.trunc(Number(index)) || 0;
  if (i < 0) i += len;
  if (i < 0 || i >= len) return undefined;
  return this[i];
}

if (!(Array.prototype as any).at) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(Array.prototype, "at", {
    value: atShim,
    writable: true,
    enumerable: false, // never show up in for…in / Object.keys
    configurable: true,
  });
}

if (!(String.prototype as any).at) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(String.prototype, "at", {
    value: atShim,
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// ── Object.hasOwn (Safari 15.4) ──────────────────────────────────────────
// Sibling ES2022 method from the same Safari release; widely used by
// modern bundled libs. Cheap insurance against a second
// "X is not a function" from the same old-iOS cohort.
if (!(Object as any).hasOwn) {
  Object.defineProperty(Object, "hasOwn", {
    value: function hasOwn(obj: object, prop: PropertyKey) {
      return Object.prototype.hasOwnProperty.call(obj, prop);
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

export {};
